import { useState } from "react"
import { ArrowLeftRight } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { LoadingState } from "@/app/components/LoadingState"
import { PageHeader } from "@/app/components/PageHeader"
import { TransactionsTable } from "./TransactionsTable"
import type { Transaction } from "./TransactionRow"

interface TransactionsResponse {
  data: Transaction[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

export function TransactionsPage() {
  const { theme } = useClientTheme()
  const { canWrite } = useCurrentUser()
  const [showDeleted, setShowDeleted] = useState(false)
  const [params] = useSearchParams()
  const tx = params.get("tx") ?? undefined

  const includeDeleted = canWrite && showDeleted
  const { data, isLoading } = useQuery<TransactionsResponse>({
    queryKey: ["transactions", includeDeleted ? "with_deleted" : "all"],
    queryFn: () =>
      api.get<TransactionsResponse>(
        `/transactions?per_page=200${includeDeleted ? "&include_deleted=1" : ""}`
      ),
  })

  const rows = data?.data ?? []

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        <PageHeader
          title="Transaction History"
          subtitle="Mineral conveyances — acquisitions & divestitures"
          theme={theme}
          icon={<ArrowLeftRight size={17} strokeWidth={1.75} />}
        />
        {isLoading ? (
          <LoadingState theme={theme} />
        ) : (
          <TransactionsTable
            data={rows}
            canWrite={canWrite}
            showDeleted={showDeleted}
            onToggleDeleted={setShowDeleted}
            initialSearch={tx}
          />
        )}
      </div>
    </div>
  )
}
